import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
  type QueryKey,
} from '@tanstack/react-query';
import service from '../service';
import type { EmailThread } from '../types/emailThread';
import { useMemo } from 'react';
import { stableStringify } from '@Utils/searchParams';

type EmailThreadResponse = {
  data: EmailThread[];
  meta: {
    count: number;
    page: {
      number: number;
      size: number;
      pageCount: number;
    };
    total: number;
  };
};
type DefaultError = unknown;

type UseEmailThreadParams<S extends Record<string, any> = Record<string, any>> =
  {
    /** Optional query string params to forward to service.getMembers */
    searchParams?: S;
    /** Override default enabled behavior */
    enabled?: boolean;
  };

export function useEmailThreads<
  TData = EmailThreadResponse,
  TError = DefaultError,
>(
  { searchParams, enabled = true }: UseEmailThreadParams = {},
  options?: Omit<
    UseQueryOptions<EmailThreadResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const stableParams = useMemo(
    () => stableStringify(searchParams),
    [searchParams],
  );
  const queryKey: QueryKey = ['emailThreads', stableParams];

  return useQuery<EmailThreadResponse, TError, TData>({
    queryKey,
    enabled: enabled,
    queryFn: async () => {
      return service.getEmailThreads(searchParams ?? {});
    },
    ...(options as any),
  });
}
